import { useCallback, useEffect, useState } from "react";

export function getErrorMessage(error: unknown) {
  const anyError = error as any;

  const validationErrors = anyError?.response?.data?.validationErrors;
  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    return validationErrors
      .map((item) => `${item.field}: ${item.message}`)
      .join(", ");
  }

  return (
    anyError?.response?.data?.message ||
    anyError?.response?.data?.details ||
    anyError?.data?.message ||
    anyError?.message ||
    "Something went wrong"
  );
}

export function useAsyncData<T>(
  loader: () => Promise<T>,
  deps: unknown[] = [],
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await loader();
      setData(response);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, deps);

  useEffect(() => {
    load();
  }, [load]);

  return {
    data,
    loading,
    error,
    reload: load,
    setData,
  };
}

export function useMutation<TPayload, TResult>(
  fn: (payload: TPayload) => Promise<TResult>,
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function mutate(payload: TPayload) {
    try {
      setLoading(true);
      setError(null);
      return await fn(payload);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  return {
    mutate,
    loading,
    error,
    clearError: () => setError(null),
  };
}

export function useVoidMutation<TResult>(fn: () => Promise<TResult>) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function mutate() {
    try {
      setLoading(true);
      setError(null);
      return await fn();
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  return {
    mutate,
    loading,
    error,
    clearError: () => setError(null),
  };
}

export function usePagination(initialPage = 0, initialSize = 10) {
  const [page, setPage] = useState(initialPage);
  const [size, setSizeValue] = useState(initialSize);

  function setSize(nextSize: number) {
    setSizeValue(nextSize);
    setPage(0);
  }

  return {
    page,
    size,
    setPage,
    setSize,
    nextPage: () => setPage((current) => current + 1),
    previousPage: () => setPage((current) => Math.max(0, current - 1)),
    reset: () => setPage(0),
  };
}

export function formatDisplayDate(value?: string | null) {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
}

export function formatDisplayDateTime(value?: string | null) {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export function formatEnum(value?: string | null) {
  if (!value) return "-";

  return value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function formatDays(value?: number | null) {
  const amount = Number(value ?? 0);
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}